// src/features/polls/PollManagerSliceSchema.ts

import { z } from "zod";
import { SurveySchema } from "@bridge-applications/eh-types";

/**
 * Who authored a message in the chat UI
 */
export const AuthorSchema = z.enum(["user", "bot"]);
export type Author = z.infer<typeof AuthorSchema>;

/**
 * UI widgets that can be rendered under a chat message bubble
 */
export const ChatWidgetSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("buttons"),
        buttons: z.array(
            z.object({
                id: z.string(),
                label: z.string(),
            }),
        ),
    }),
    z.object({
        kind: z.literal("file-select"),
        accept: z.array(z.string()).optional(),
        maxFiles: z.number().optional(),
        minFiles: z.number().optional(),
        maxFileSizeMB: z.number().optional(),
    }),
    z.object({
        kind: z.literal("options"),
        options: z.array(
            z.object({
                id: z.string(),
                label: z.string(),
            }),
        ),
        multiSelect: z.boolean().optional(),
    }),
    z.object({
        kind: z.literal("templates"),
        templates: z.array(
            z.object({
                id: z.string(),
                title: z.string(),
                description: z.string(),
                pollType: z.enum(["ab", "normal"]).optional(),
            }),
        ),
    }),
    z.object({
        kind: z.literal("template-card"),
        templateId: z.string(),
        title: z.string(),
        description: z.string().optional(),
        pollType: z.enum(["ab", "normal"]).optional(),
    }),
    z.object({
        kind: z.literal("open-form"),
        formId: z.string(),
        title: z.string(),
        description: z.string().optional(),
        buttonLabel: z.string().optional(),
    }),
    z.object({
        kind: z.literal("text-collection"),
        stepId: z.string(),
        dataKey: z.string(),
        placeholder: z.string().optional(),
        helperText: z.string().optional(),
        minEntries: z.number().optional(),
        maxEntries: z.number().optional(),
        addLabel: z.string().optional(),
        confirmLabel: z.string().optional(),
        entryLabel: z.string().optional(),
        inputMode: z.enum(["single-line", "multi-line"]).optional(),
        autoSubmitOnMax: z.boolean().optional(),
        allowDuplicates: z.boolean().optional(),
    }),
]);
export type ChatWidget = z.infer<typeof ChatWidgetSchema>;

/**
 * Simple flow state for the poll-builder "system flows"
 */
export const FlowStateSchema = z.object({
    mode: z.enum(["system-flow", "template-flow", "llm"]),
    activeFlowId: z.string().optional(),
    activeStepId: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
});
export type FlowState = z.infer<typeof FlowStateSchema>;

const WidgetSnapshotSchema = z.object({
    flowState: FlowStateSchema.optional(),
    surveyDraft: SurveySchema.optional(),
});
export type WidgetSnapshot = z.infer<typeof WidgetSnapshotSchema>;

/**
 * A single chat message as shown in PollChat
 * Keep it aligned with your PollChat.tsx props.
 */
export const ChatMessageSchema = z.object({
    id: z.string(),
    author: AuthorSchema,
    text: z.string(),
    /**
     * store as number (ms) in LS, but accept other forms when hydrating
     */
    createdAt: z.union([z.number(), z.string(), z.date()]),
    /**
     * extra info you might want from the LLM exchange (optional)
     */
    meta: z
        .object({
            fromLLM: z.boolean().optional(),
            // e.g. tool calls, raw response, etc.
            raw: z.unknown().optional(),
            /**
             * flow helpers
             */
            flowStepId: z.string().optional(),
            widget: ChatWidgetSchema.optional(),
            widgetSnapshot: WidgetSnapshotSchema.optional(),
            widgetInstanceId: z.string().optional(),
        })
        .optional(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/**
 * One conversation == one poll-building session
 */
export const PollConversationSchema = z.object({
    id: z.string(), // usually your pollId / taskId
    title: z.string().optional(),
    messages: z.array(ChatMessageSchema).default([]),
    /**
     * what the user is currently typing
     * (so you can restore the composer textarea too)
     */
    draftInput: z.string().nullable().optional(),
    /**
     * the current survey draft for this conversation
     * this reuses your app-level SurveySchema
     */
    surveyDraft: SurveySchema.optional(),
    /**
     * bookkeeping for LLM threads (if your backend uses it)
     */
    llmContext: z
        .object({
            threadId: z.string().optional(),
            lastBotMessageId: z.string().optional(),
        })
        .optional(),
    /**
     * mini state machine that owns the non-LLM flows
     */
    flowState: FlowStateSchema.optional(),
    /**
     * per-form draft values so users don't lose progress when closing/reopening a form
     */
    formDrafts: z
        .record(z.string(), z.record(z.string(), z.unknown()))
        .optional()
        .default({}),
    createdAt: z.number(), // ms
    updatedAt: z.number(), // ms
});
export type PollConversation = z.infer<typeof PollConversationSchema>;

const PollManagerMetaSchema = z.object({
    leaseToken: z.string().nullable().optional(),
    lastPublishStatus: z.enum(["PUBLISHED"]).nullable().optional(),
    lastPublishAt: z.number().optional(),
});
export type PollManagerMeta = z.infer<typeof PollManagerMetaSchema>;

/**
 * The whole slice that goes into Redux and LS
 */
export const PollManagerSliceSchema = z.object({
    /**
     * which conversation/poll is live in the UI
     */
    activeConversationId: z.string().nullable(),
    /**
     * all conversations keyed by id
     */
    conversations: z.record(z.string(), PollConversationSchema),
    /**
     * lightweight session metadata
     */
    meta: PollManagerMetaSchema.optional(),
});
export type PollManagerSlice = z.infer<typeof PollManagerSliceSchema>;
